import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ColumnDef } from "@tanstack/react-table";
import { Link, useNavigate } from "react-router-dom";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import { TablePaginate } from "../../../components/table";
import { useNotifyContext, NotifyType } from "../../../contexts/NotifyContext";
import { AlertModal, AlertType, useAlert } from "../../../hooks/useAlert";

import { isAdmin } from "../../auth";
import { Button } from "../../../components/form";
import { AuthShield } from "../../auth/core";
import { Head } from "../../../components/core";

type Role = {
  roleName: string;
};
type User = {
  id: string;
  fullName: string;
  email: string;
  roles: Role[];
  createdAt: string;
  status: boolean;
};

const FETCH_USERS = gql`
  query FetchUsers {
    fetchUsers {
      id
      fullName
      email
      roles {
        roleName
      }
      createdAt
      status
    }
  }
`;

const UserList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [fetchUsers, { loading }] = useLazyQuery(FETCH_USERS);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    fetchUsers()
      .then(({ data, error }) => {
        if (data?.fetchUsers) {
          const updatedUsers = data.fetchUsers.map((item: User) => ({
            ...item,
            createdAt: DateTime.fromISO(item.createdAt, {
              locale: i18n.language,
            }).toLocaleString(DateTime.DATE_MED),
          }));
          setUsers(updatedUsers);
        } else {
          return navigate("/error/401", {
            state: {
              message: error
                ? error.message
                : "User read permission(s) is required to access this page.",
            },
          });
        }
      })
      .catch((error) => {
        return navigate("/error/500", {
          state: {
            error,
            message: error.message,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers]);

  const DELETE_USER = gql`
    mutation UserDelete($id: ID!) {
      userDelete(input: { id: $id }) {
        user {
          id
          fullName
        }
      }
    }
  `;
  const [deleteUser] = useMutation(DELETE_USER);
  const handleDelete = async (id: string) => {
    return await deleteUser({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.userDelete) {
          const deletedUser = data?.userDelete?.user;
          const newUsers = [...users];
          const index = newUsers.findIndex((user) => user.id === id);
          newUsers.splice(index, 1);
          setUsers(newUsers);
          addNotify({
            type: NotifyType.SUCCESS,
            title: `User ${deletedUser.fullName} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "User delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "User delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "fullName",
        header: "Full Name",
        meta: {
          className: "md:w-48",
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (info: any) => <span>{info.getValue()}</span>,
      },
      {
        accessorKey: "roles",
        header: "Role",
        cell: (info: any) =>
          info
            .getValue()
            .map((r: Role) => r.roleName)
            .join(", "),
        enableSorting: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: (props) =>
          props.row.original.roles.find((r) => !isAdmin(r)) ? (
            <div>
              <AuthShield access={["update-users"]}>
                <Link
                  to={`/user-management/users/${props.row.original.id}`}
                  className="mr-4 inline-flex appearance-none py-2 text-primary-700 hover:text-primary-900"
                >
                  <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                  <span className="sr-only">
                    Edit, {props.row.original.fullName}
                  </span>
                </Link>
              </AuthShield>
              <AuthShield access={["destroy-users"]}>
                <button
                  type="button"
                  className="appearance-none py-2 text-red-700 hover:text-red-900"
                  onClick={() => {
                    setAlert({
                      id: props.row.original.id,
                      name: props.row.original.fullName,
                    });
                  }}
                >
                  <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                  <span className="sr-only">
                    Delete, {props.row.original.fullName}
                  </span>
                </button>
              </AuthShield>
            </div>
          ) : null,
        meta: {
          className: "w-auto md:w-20",
        },
      },
    ],
    [i18n.language]
  );

  return (
    <>
      <Head
        title="Users"
        heading="Users"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Users",
            href: "/user-management/users",
          },
          {
            name: UserListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">Users List</h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the users in your account including their name,
              title, email and user.
            </p>
          </div>
        </div>
        <TablePaginate
          data={users}
          columns={columns}
          loading={loading}
          className="mt-4"
          renderSubComponent={() => (
            <AuthShield access={["create-users"]}>
              <Link to="/user-management/users/add">
                <Button>Add user</Button>
              </Link>
            </AuthShield>
          )}
        />
      </div>
    </>
  );
};

export default UserList;
export const UserListResource: ResourceProps = {
  name: "Users List",
  description: "A list of users",
  access: ["read-users"],
  path: "users",
};
